
import { Constants_gridSize, getScrollProps, viewIsAfterUpdateScroll, wheelZoom, putScrollProps } from "./Sheet.js";
import { CursorType__Text, XYPos } from "../Common/CommonTypes.js";
import { defaultOf, equals, Lazy } from "../fable_modules/fable-library-js.5.13.0/Util.js";
import { DrawModelType_SheetT_Msg } from "../Model/DrawModelType.js";
import { Msg } from "../Model/ModelType.js";
import { value as value_1, ofNullable } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { toFail, printf, toText } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { cons, fold, reverse, item, map, append as append_1, singleton as singleton_1, ofArrayWithTail, ofArray, empty } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { empty as empty_1, singleton, append, delay, toList } from "../fable_modules/fable-library-js.5.13.0/Seq.js";
import { DOMAttr, CSSProp, Prop, HTMLAttr } from "../fable_modules/Fable.React.9.4.0/Fable.React.Props.fs.js";
import { keyValueList } from "../fable_modules/fable-library-js.5.13.0/MapUtil.js";
import { portCircle, Line, makeLine, defaultCircle, makePartArcAttr, makeLineAttr, Circle, makeCircle, Path, defaultPath, makeAnyPath, Polygon, makePolygon, MouseOp } from "../Common/DrawHelpers.js";
import * as react from "react";
import { view as view_1 } from "./BusWire.js";
import { max } from "../fable_modules/fable-library-js.5.13.0/Double.js";
import { snapIndicatorLineY as snapIndicatorLineY_1, snapIndicatorLineX as snapIndicatorLineX_1 } from "./SheetSnap.js";
import { item as item_1, map as map_1 } from "../fable_modules/fable-library-js.5.13.0/Array.js";
import { rangeDouble } from "../fable_modules/fable-library-js.5.13.0/Range.js";

let physicalModifierHeld = false;

export function setPhysicalModifierHeld(value) {
    physicalModifierHeld = value;
}

export function isPhysicalModifierHeld() {
    return physicalModifierHeld;
}

/**
 * This actually writes to the DOM a new scroll position.
 * In the special case that DOM has not yet been created it does nothing.
 */
export function writeCanvasScroll(scrollPos) {
    return putScrollProps(scrollPos);
}

let mountedCanvas = undefined;

let modelScrollPos = new XYPos(0, 0);

const canvasRef = new Lazy(() => ((el) => {
    if (equals(el, defaultOf())) {
        mountedCanvas = undefined;
    }
    else {
        const el_1 = el;
        if ((mountedCanvas == null) ? true : !(mountedCanvas === el_1)) {
            mountedCanvas = el_1;
            const canvas = el_1;
            canvas.scrollLeft = modelScrollPos.X;
            canvas.scrollTop = modelScrollPos.Y;
        }
    }
}));

export function getDrawBlockPos(ev, headerHeight, sheetModel) {
    return new XYPos((ev.pageX + sheetModel.ScreenScrollPos.X) / sheetModel.Zoom, ((ev.pageY - headerHeight) + sheetModel.ScreenScrollPos.Y) / sheetModel.Zoom);
}

/**
 * Every wheel event over the canvas is decided here, and this is a NATIVE listener - registered
 * non-passive on the document by Renderer.appSubscriptions - not a React one, because React 18
 * attaches its wheel listeners passively and a passive listener cannot preventDefault.
 * 
 * A wheel that classifies as a zoom gesture - real Ctrl/Cmd held, or the synthetic ctrlKey
 * Chromium puts on a trackpad pinch - must zoom and only zoom, so its native default (scrolling
 * the canvas, or the browser's own page zoom) is suppressed even when the factor is too small
 * to be worth dispatching: Ctrl + a diagonal two-finger scroll must not creep sideways. An
 * unmodified wheel is left entirely alone, so the canvas scrolls natively as it always has.
 */
export function onCanvasWheel(dispatch, e) {
    let matchValue;
    const ev = e;
    if ((matchValue = document.getElementById("Canvas"), equals(matchValue, defaultOf()) ? false : matchValue.contains(ev.target))) {
        const matchValue_1 = wheelZoom(ev.deltaMode, ev.deltaY, ev.ctrlKey ? true : ev.metaKey, isPhysicalModifierHeld());
        let matchResult, zoomFactor;
        if (matchValue_1 != null) {
            if (matchValue_1.tag === 1) {
                matchResult = 1;
                zoomFactor = matchValue_1.fields[0];
            }
            else {
                matchResult = 1;
                zoomFactor = matchValue_1.fields[0];
            }
        }
        else {
            matchResult = 0;
        }
        switch (matchResult) {
            case 0: {
                break;
            }
            case 1: {
                ev.preventDefault();
                if (Math.abs(zoomFactor - 1) > 0.0001) {
                    dispatch(new Msg(/* Sheet */ 3, [new DrawModelType_SheetT_Msg(/* PreciseZoom */ 6, [zoomFactor])]));
                }
                break;
            }
        }
    }
}

/**
 * Clicking the canvas should move keyboard focus away from text inputs in the browser.
 */
export function focusCanvas() {
    const option_1 = ofNullable(document.getElementById("Canvas"));
    if (option_1 != null) {
        const element = option_1;
        element.focus();
    }
}

/**
 * Is the mouse button currently down?
 */
export function mDown(ev) {
    return ev.buttons !== 0;
}

/**
 * Dispatch a MouseMsg (compensated for zoom)
 */
export function mouseOp(op, ev, dispatch, headerHeight) {
    if (~~ev.button === 0) {
        dispatch(new DrawModelType_SheetT_Msg(/* MouseMsgOrig */ 8, [ev, op, headerHeight]));
    }
}

/**
 * This function zooms an SVG canvas by transforming its content and altering its size.
 * Currently the zoom expands based on top left corner.
 */
export function displaySvgWithZoom(model, headerHeight, style, svgReact, dispatch) {
    let scroll, props_2, children_2, props;
    let sizeInPixels;
    const arg = model.CanvasSize * model.Zoom;
    sizeInPixels = toText(printf("%.2fpx"))(arg);
    const cursorText = CursorType__Text(model.CursorType);
    const firstView = viewIsAfterUpdateScroll();
    viewIsAfterUpdateScroll(false);
    const scrollOpt = getScrollProps();
    const scrollAttrL = (scrollOpt != null) ? (firstView ? empty() : ((scroll = scrollOpt, ofArray([["scrollleft", scroll.X], ["scrolltop", scroll.Y]])))) : empty();
    modelScrollPos = model.ScreenScrollPos;
    const attrs = toList(delay(() => append(singleton(new HTMLAttr(/* Id */ 99, ["Canvas"])), delay(() => append(singleton(new HTMLAttr(/* TabIndex */ 156, [0])), delay(() => append(singleton(new Prop(/* Ref */ 1, [canvasRef.Value])), delay(() => append(singleton(["style", keyValueList(ofArrayWithTail([new CSSProp(/* Cursor */ 123, [cursorText]), new CSSProp(/* Outline */ 264, ["none"])], style), 1)]), delay(() => append(singleton(new DOMAttr(/* OnMouseDown */ 51, [(ev) => {
        focusCanvas();
        mouseOp(MouseOp.Down, ev, dispatch, headerHeight);
    }])), delay(() => append(singleton(new DOMAttr(/* OnMouseUp */ 57, [(ev_1) => {
        mouseOp(MouseOp.Up, ev_1, dispatch, headerHeight);
    }])), delay(() => append(singleton(new DOMAttr(/* OnMouseMove */ 54, [(ev_2) => {
        mouseOp(mDown(ev_2) ? MouseOp.Drag : MouseOp.Move, ev_2, dispatch, headerHeight);
    }])), delay(() => append(singleton(new DOMAttr(/* OnScroll */ 63, [(_arg) => {
        if (!firstView) {
            const option_1 = getScrollProps();
            if (option_1 != null) {
                dispatch(new DrawModelType_SheetT_Msg(/* UpdateScrollPosFromCanvas */ 12, [option_1]));
            }
        }
    }])), delay(() => {
        const matchValue_1 = !firstView;
        let matchResult;
        if (matchValue_1) {
            if (scrollOpt != null) {
                matchResult = 0;
            }
            else {
                matchResult = 1;
            }
        }
        else {
            matchResult = 1;
        }
        switch (matchResult) {
            case 0: {
                putScrollProps(scrollOpt);
                return empty_1();
            }
            default: {
                return empty_1();
            }
        }
    }))))))))))))))))));
    const zoomCommitRef = (model.PendingZoomCenter == null) ? empty() : singleton_1(new Prop(/* Ref */ 1, [(element) => {
        if (!equals(element, defaultOf())) {
            dispatch(DrawModelType_SheetT_Msg.ApplyPendingZoomCenter);
        }
    }]));
    const props_4 = append_1(scrollAttrL, attrs);
    const children_4 = [(props_2 = append_1(zoomCommitRef, ofArray([["style", {
        height: sizeInPixels,
        width: sizeInPixels,
        display: "block",
    }], new HTMLAttr(/* Id */ 99, ["DrawBlockSVGTop"])])), (children_2 = [(props = [["style", {
        transform: toText(printf("scale(%f)"))(model.Zoom),
    }]], react.createElement("g", keyValueList(props, 1), ...svgReact))], react.createElement("svg", keyValueList(props_2, 1), ...children_2)))];
    return react.createElement("div", keyValueList(props_4, 1), ...children_4);
}

/**
 * View function, displays symbols / wires and possibly also a grid / drag-to-select box / connecting ports line / snap-to-grid visualisation
 * `overlay` is drawn on top of the wires, in draw block coordinates, so it pans and zooms with
 * the schematic. It exists for things the draw block cannot work out for itself - at present the
 * value on the wire under the cursor, which only the UI layer knows, because only it knows a
 * simulation is running. Empty the rest of the time.
 * 
 * `highlighted` arrives the same way and for the same reason: symbols and wires to show picked
 * out because of something outside the draw block - at present the waveform whose name the
 * pointer is on. Derived by the caller on every render, never stored here; see
 * DrawModelType.Highlighted.
 */
export function view(model, headerHeight, style, overlay, highlighted, dispatch) {
    let children_4, children_2;
    const wireSvg = view_1(model.Wire, highlighted, (wMsg) => {
        dispatch(new DrawModelType_SheetT_Msg(/* Wire */ 0, [wMsg]));
    });
    const wholeCanvas = (`${max(100, 100 / model.Zoom)}`) + "%";
    const snapIndicatorLineX = snapIndicatorLineX_1(model, wholeCanvas);
    const snapIndicatorLineY = snapIndicatorLineY_1(model, wholeCanvas);
    let grid;
    const children_8 = [(children_4 = [(children_2 = [react.createElement("path", {
        d: `M ${Constants_gridSize} 0 L 0 0 0 ${Constants_gridSize}`,
        fill: "None",
        stroke: "Gray",
        strokeWidth: "0.5",
    })], react.createElement("pattern", {
        id: "Grid",
        width: `${Constants_gridSize}`,
        height: `${Constants_gridSize}`,
        patternUnits: "userSpaceOnUse",
    }, ...children_2))], react.createElement("defs", {}, ...children_4)), react.createElement("rect", {
        width: wholeCanvas,
        height: wholeCanvas,
        fill: "url(#Grid)",
    })];
    grid = react.createElement("svg", {
        width: wholeCanvas,
        height: wholeCanvas,
        xmlSpace: "http://www.w3.org/2000/svg",
    }, ...children_8);
    let dragToSelectBox;
    const patternInput = model.DragToSelectBox;
    const fY = patternInput.TopLeft.Y;
    const fX = patternInput.TopLeft.X;
    const fW = patternInput.W;
    const fH = patternInput.H;
    dragToSelectBox = makePolygon(`${fX},${fY} ${fX + fW},${fY} ${fX + fW},${fY + fH} ${fX},${fY + fH}`, new Polygon("Black", "0.1px", 0.05, "Blue"));
    const drawAnnotation = (symbol, boxH_1, boxW_1) => {
        let arg_2, left_3, right_3, centre, list_1, left_1, right_1;
        const transform = symbol.STransform;
        const W = symbol.Component.W;
        const H = symbol.Component.H;
        const createAnyPath = (startingPoint, pathAttr, colour, strokeWidth_1, outlineColour_1) => singleton_1(makeAnyPath(startingPoint, pathAttr, new Path(outlineColour_1, strokeWidth_1, defaultPath.StrokeDashArray, "round", colour)));
        const iconDisc = (centre_1, radius) => singleton_1(makeCircle(centre_1.X, centre_1.Y, new Circle(radius, "#b9c0c7", "1.0", 0.95, "#f7f8fa")));
        const matchValue_3 = symbol.Annotation;
        if (matchValue_3 != null) {
            const a = matchValue_3;
            if (a.tag === 1) {
                let curvyShape;
                const points = map((array_3) => map_1((tupledArg_1) => (new XYPos(tupledArg_1[0], tupledArg_1[1])), array_3), ofArray([[[W / 3, (7 * H) / 9], [0, -H / 9], [-W / 4, H / 6], [W / 4, H / 6], [0, -H / 9], [0, -W / 2], [0, W / 2], [-W / 4, 0], [0, H / 9], [W / 4, 0], [0.001, (7 * W) / 18], [0.001, (-7 * W) / 18]], [[(2 * W) / 3, (7 * H) / 9], [0, -H / 9], [W / 4, H / 6], [-W / 4, H / 6], [0, -H / 9], [0.001, -W / 2], [0.001, W / 2], [W / 4, 0], [0, H / 9], [-W / 4, 0], [0, (7 * W) / 18], [0, (-7 * W) / 18]]]));
                const matchValue_4 = transform.Rotation;
                const matchValue_5 = transform.flipped;
                curvyShape = ((matchValue_4.tag === 1) ? item(1, points) : ((matchValue_4.tag === 2) ? (matchValue_5 ? item(0, points) : item(2, points)) : ((matchValue_4.tag === 3) ? item(3, points) : (matchValue_5 ? item(2, points) : item(0, points)))));
                const arrowHead = ((makeLineAttr(item_1(1, curvyShape).X, item_1(1, curvyShape).Y) + makeLineAttr(item_1(2, curvyShape).X, item_1(2, curvyShape).Y)) + makeLineAttr(item_1(3, curvyShape).X, item_1(3, curvyShape).Y)) + makeLineAttr(item_1(4, curvyShape).X, item_1(4, curvyShape).Y);
                const arcAttr1 = makePartArcAttr(W / 2, item_1(5, curvyShape).Y, item_1(5, curvyShape).X, item_1(6, curvyShape).Y, item_1(6, curvyShape).X);
                const touchUp = (makeLineAttr(item_1(7, curvyShape).X, item_1(7, curvyShape).Y) + makeLineAttr(item_1(8, curvyShape).X, item_1(8, curvyShape).Y)) + makeLineAttr(item_1(9, curvyShape).X, item_1(9, curvyShape).Y);
                const arcAttr2 = makePartArcAttr((7 * W) / 18, item_1(10, curvyShape).Y, item_1(10, curvyShape).X, item_1(11, curvyShape).Y, item_1(11, curvyShape).X);
                let centre_2;
                const left_2 = symbol.Pos;
                const right_2 = new XYPos(W / 2, H / 2);
                centre_2 = (new XYPos(left_2.X + right_2.X, left_2.Y + right_2.Y));
                return append_1(iconDisc(centre_2, 15), (arg_2 = createAnyPath((left_3 = symbol.Pos, (right_3 = item_1(0, curvyShape), new XYPos(left_3.X + right_3.X, left_3.Y + right_3.Y))), ((arrowHead + arcAttr1) + touchUp) + arcAttr2, "#5a6169", "0.6", "#5a6169"), ((centre = centre_2, (factor) => ((elements) => {
                    let props_10;
                    return singleton_1((props_10 = [["style", {
                        transform: `translate(${centre.X}px, ${centre.Y}px) scale(${factor}) translate(${-centre.X}px, ${-centre.Y}px)`,
                    }]], react.createElement("g", keyValueList(props_10, 1), ...elements)));
                })))(0.78)(arg_2)));
            }
            else {
                const shapePointsPre = map((tupledArg) => {
                    const boxW = boxW_1;
                    const boxH = boxH_1;
                    const point = new XYPos(tupledArg[0], tupledArg[1]);
                    const diagonal = Math.sqrt(Math.pow(boxW, 2) + Math.pow(boxH, 2));
                    const cosTheta = -(boxW / diagonal);
                    const sinTheta = boxH / diagonal;
                    const y = point.Y;
                    const x = point.X;
                    return new XYPos((x * cosTheta) - (y * sinTheta), (y * cosTheta) + (x * sinTheta));
                }, ofArray([[4.5, -2], [4.5, -5], [10.5, 0], [4.5, 5], [4.5, 2], [-4.5, 2], [-4.5, 5], [-10.5, 0], [-4.5, -5], [-4.5, -2], [4.5, -2]]));
                const shapePoints = reverse((list_1 = toList(rangeDouble(1, 1, 10)), fold((lst, x_2) => {
                    let left, right;
                    return cons((left = item(x_2, shapePointsPre), (right = item(x_2 - 1, shapePointsPre), new XYPos(left.X - right.X, left.Y - right.Y))), lst);
                }, singleton_1(item(0, shapePointsPre)), list_1)));
                const arrowHeadTopRight = (((makeLineAttr(item(1, shapePoints).X, item(1, shapePoints).Y) + makeLineAttr(item(2, shapePoints).X, item(2, shapePoints).Y)) + makeLineAttr(item(3, shapePoints).X, item(3, shapePoints).Y)) + makeLineAttr(item(4, shapePoints).X, item(4, shapePoints).Y)) + makeLineAttr(item(5, shapePoints).X, item(5, shapePoints).Y);
                const arrowHeadBottomLeft = (((makeLineAttr(item(6, shapePoints).X, item(6, shapePoints).Y) + makeLineAttr(item(7, shapePoints).X, item(7, shapePoints).Y)) + makeLineAttr(item(8, shapePoints).X, item(8, shapePoints).Y)) + makeLineAttr(item(9, shapePoints).X, item(9, shapePoints).Y)) + makeLineAttr(item(10, shapePoints).X, item(10, shapePoints).Y);
                return append_1(iconDisc(symbol.Pos, 13), createAnyPath((left_1 = symbol.Pos, (right_1 = item(0, shapePoints), new XYPos(left_1.X + right_1.X, left_1.Y + right_1.Y))), arrowHeadTopRight + arrowHeadBottomLeft, "#5a6169", "0.6", "#5a6169"));
            }
        }
        else {
            return toFail(printf("Should not be getting Annotation = None for drawing scalingBox buttons "));
        }
    };
    let scalingBox;
    if (model.ScalingBox == null) {
        scalingBox = append_1(singleton_1(makeAnyPath(new XYPos(0, 0), makeLineAttr(0, 0), defaultPath)), singleton_1(makeCircle(0, 0, new Circle(0, defaultCircle.Stroke, defaultCircle.StrokeWidth, defaultCircle.FillOpacity, defaultCircle.Fill))));
    }
    else {
        const patternInput_3 = value_1(model.ScalingBox).ScalingBoxBound;
        const fW_1 = patternInput_3.W;
        const fH_1 = patternInput_3.H;
        scalingBox = append_1(singleton_1(makeAnyPath(new XYPos((patternInput_3.TopLeft.X + 50) + fW_1, patternInput_3.TopLeft.Y - 46.5), ((makeLineAttr(0, fH_1 + 96.5) + makeLineAttr(-(fW_1 + 100), 0)) + makeLineAttr(0, -fH_1 - 100)) + makeLineAttr(fW_1 + 96.5, 0), new Path(defaultPath.Stroke, defaultPath.StrokeWidth, "4,4", defaultPath.StrokeLinecap, defaultPath.Fill))), append_1(drawAnnotation(value_1(model.ScalingBox).RotateDeg270Button, fH_1 + 100, fW_1 + 100), append_1(drawAnnotation(value_1(model.ScalingBox).RotateDeg90Button, fH_1 + 100, fW_1 + 100), drawAnnotation(value_1(model.ScalingBox).ScaleButton, fH_1 + 100, fW_1 + 100))));
    }
    let connectingPortsWire;
    const patternInput_4 = model.ConnectPortsLine;
    const y2 = patternInput_4[1].Y;
    const x2 = patternInput_4[1].X;
    connectingPortsWire = ofArray([makeLine(patternInput_4[0].X, patternInput_4[0].Y, x2, y2, new Line("Green", "2.0px", "5, 5")), makeCircle(x2, y2, new Circle(portCircle.R, portCircle.Stroke, portCircle.StrokeWidth, portCircle.FillOpacity, "Green"))]);
    const displayElements = model.ShowGrid ? append_1(ofArray([grid, wireSvg]), overlay) : append_1(singleton_1(wireSvg), overlay);
    const snaps = append_1(snapIndicatorLineX, snapIndicatorLineY);
    const matchValue_8 = model.Action;
    const matchValue_9 = model.ScalingBox;
    let matchResult;
    switch (matchValue_8.tag) {
        case 0: {
            matchResult = 0;
            break;
        }
        case 8: {
            if (matchValue_9 != null) {
                matchResult = 2;
            }
            else {
                matchResult = 1;
            }
            break;
        }
        case 9: {
            if (matchValue_9 != null) {
                matchResult = 2;
            }
            else {
                matchResult = 1;
            }
            break;
        }
        case 5: {
            if (matchValue_9 != null) {
                matchResult = 4;
            }
            else {
                matchResult = 3;
            }
            break;
        }
        case 3: {
            matchResult = 5;
            break;
        }
        case 7: {
            matchResult = 6;
            break;
        }
        case 12: {
            matchResult = 7;
            break;
        }
        default:
            if (matchValue_9 != null) {
                matchResult = 8;
            }
            else {
                matchResult = 9;
            }
    }
    switch (matchResult) {
        case 0:
            return displaySvgWithZoom(model, headerHeight, style, append_1(displayElements, singleton_1(dragToSelectBox)), dispatch);
        case 1:
            return displaySvgWithZoom(model, headerHeight, style, append_1(displayElements, connectingPortsWire), dispatch);
        case 2:
            return displaySvgWithZoom(model, headerHeight, style, append_1(displayElements, append_1(scalingBox, connectingPortsWire)), dispatch);
        case 3:
            return displaySvgWithZoom(model, headerHeight, style, append_1(displayElements, snaps), dispatch);
        case 4:
            return displaySvgWithZoom(model, headerHeight, style, append_1(displayElements, append_1(snaps, scalingBox)), dispatch);
        case 5:
            return displaySvgWithZoom(model, headerHeight, style, append_1(displayElements, append_1(snaps, scalingBox)), dispatch);
        case 6:
            return displaySvgWithZoom(model, headerHeight, style, append_1(displayElements, snaps), dispatch);
        case 7:
            return displaySvgWithZoom(model, headerHeight, style, append_1(displayElements, scalingBox), dispatch);
        case 8:
            return displaySvgWithZoom(model, headerHeight, style, append_1(displayElements, scalingBox), dispatch);
        default:
            return displaySvgWithZoom(model, headerHeight, style, displayElements, dispatch);
    }
}

